import React, { useState } from 'react';
import { Upload, Button, Alert, Progress } from 'antd';
import { InboxOutlined, UploadOutlined } from '@ant-design/icons';
import { UploadExcelApi } from '../../api/bulk-plan/UploadExcelApi';
import { openNotificationError } from '../../helpers/NotificationHelpers';

const { Dragger } = Upload;

function ExcelUploader({ performanceCycleId, onSuccess, disabled }) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileList, setFileList] = useState([]);

  // Handle file upload
  async function handleUpload(file) {
    if (!performanceCycleId) {
      openNotificationError('bottomRight', 'Please select a performance cycle first');
      return false;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const response = await UploadExcelApi(file, performanceCycleId);

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.error) {
        openNotificationError('bottomRight', response.message);
        setFileList([]);
      } else {
        onSuccess(response);
      }
    } catch (error) {
      openNotificationError('bottomRight', 'Failed to upload file');
      setFileList([]);
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }

    return false; // Prevent default upload behavior
  }

  // Handle file selection
  function handleChange(info) {
    let newFileList = [...info.fileList];
    // Limit to 1 file
    newFileList = newFileList.slice(-1);
    setFileList(newFileList);
  }

  // Validate file before upload
  function beforeUpload(file) {
    const isExcel =
      file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.type === 'application/vnd.ms-excel' ||
      file.name.endsWith('.xlsx') ||
      file.name.endsWith('.xls');

    if (!isExcel) {
      openNotificationError('bottomRight', 'You can only upload Excel files (.xlsx or .xls)');
      return false;
    }

    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      openNotificationError('bottomRight', 'File must be smaller than 10MB');
      return false;
    }

    return true;
  }

  return (
    <div>
      <Alert
        message="Excel File Format"
        description={
          <div>
            <p>Your Excel file should contain the following columns:</p>
            <ul>
              <li><strong>Title</strong> - Plan title (required)</li>
              <li><strong>Unit</strong> - Department/Unit (required, use "CSE" for top plans)</li>
              <li><strong>Assign to</strong> - Position to assign the plan (required)</li>
              <li><strong>Target</strong> - Target value (required)</li>
              <li><strong>Target Date</strong> - Target completion date (required)</li>
              <li><strong>Description</strong> - Plan description (optional)</li>
              <li><strong>Plan Group</strong> - Plan group (optional)</li>
              <li><strong>Target Scale</strong> - Measurement scale (optional)</li>
              <li><strong>SD Unit</strong> - Scale unit (optional)</li>
            </ul>
            <p style={{ marginTop: 8, fontWeight: 500 }}>
              Plans with Unit = "CSE" will be treated as Top Plans. The plan immediately after a Top Plan becomes its Cascade Plan.
            </p>
          </div>
        }
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Dragger
        name="file"
        multiple={false}
        fileList={fileList}
        beforeUpload={beforeUpload}
        customRequest={({ file }) => handleUpload(file)}
        onChange={handleChange}
        disabled={disabled || uploading}
        accept=".xlsx,.xls"
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">
          Click or drag Excel file to this area to upload
        </p>
        <p className="ant-upload-hint">
          Support for .xlsx and .xls files up to 10MB
        </p>
      </Dragger>

      {uploading && uploadProgress > 0 && (
        <div style={{ marginTop: 16 }}>
          <Progress percent={uploadProgress} status={uploadProgress === 100 ? 'success' : 'active'} />
          <p style={{ textAlign: 'center', marginTop: 8, color: '#666' }}>
            {uploadProgress < 100 ? 'Uploading and parsing file...' : 'Processing complete!'}
          </p>
        </div>
      )}
    </div>
  );
}

export default ExcelUploader;
